import React, { useEffect, useState } from "react";
import Notification from "../../../Widgets/notification/Notification";
import ProfileButton from "../../../Widgets/profile-header/ProfileButton";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { getHospitalData } from "../../../../services/employee.service";
import { useActivityTracker } from "../../../../hooks/useActivityTracker";

export default function ScheduleHeader({ isLoginRoute }: any) {
  useActivityTracker();
  const { User } = useSelector((state: RootState) => state.auth);
  const [hospital, setHospital] = useState<any>();
  const location = useLocation().pathname;
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const fetchHospitalData = async () => {
    try {
      const response: any = await getHospitalData(User?.hospital as string);
      if (response) {
        setHospital(response.res.hospitalDB);
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (User?.hospital) fetchHospitalData();
  }, [User]);
  return (
    <>
      <header
        className={`py-3 px-4 md:py-4 md:px-7 absolute top-0 right-0 left-0 z-40 w-full ${
          isLoginRoute ? "hidden" : "block"
        }`}
      >
        <nav className="flex justify-between items-center">
          {/* Left: logo + hospital */}
          <div className="flex justify-start items-center gap-2 md:gap-4">
            <NavLink to="/" className="cursor-pointer">
              <img src={"images/logo.svg"} alt="logo" className="h-7 md:h-8 brightness-0 invert-[1]"/>
            </NavLink>
            <div className="lg:flex hidden items-center rounded-xl px-2 py-1">
              {hospital?.logo && (
                <img
                  className="me-2 w-[37px] h-[37px] rounded-full object-cover"
                  src={hospital.logo}
                  alt={hospital.name}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              )}
              <div>
                <p className="text-white text-moon-14 font-semibold">
                  {hospital ? hospital.name : "Hospital name"}
                </p>
                <p className="text-white text-moon-9">{User?.branchName}</p>
              </div>
            </div>
          </div>

          {/* Center: nav links — hidden on mobile */}
          <ul className="hidden md:flex items-center s2xl:space-x-9 space-x-5 text-[17px] cursor-pointer text-whitelight">
            <li className={`hover:text-white ${location === "/" ? "text-white underline" : ""}`}>
              <NavLink to="/">Home</NavLink>
            </li>
            <li className={`hover:text-white ${location === "/time-off" ? "text-white underline" : ""}`}>
              <NavLink to="/time-off">Time-off</NavLink>
            </li>
            <li className={`hover:text-white ${location === "/leaves" ? "text-white underline" : ""}`}>
              <NavLink to="/leaves">Leaves</NavLink>
            </li>
            <li className={`hover:text-white ${location === "/swap" ? "text-white underline" : ""}`}>
              <NavLink to="/swap">Swap</NavLink>
            </li>
          </ul>

          {/* Right: notification + profile */}
          <ul className="flex items-center space-x-2 text-xs">
            <li className="flex">
              <button
                onClick={() => setGuideOpen(true)}
                title="Help Guide"
                className="inline-flex items-center justify-center w-[36px] h-[36px] rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="9.5" stroke="currentColor" strokeWidth="1.8"/><path d="M9 8.5a2 2 0 0 1 4 0c0 1.105-.895 2-2 2V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><circle cx="11" cy="15.75" r="1" fill="currentColor"/></svg>
              </button>
            </li>
            <li className="flex">
              <Notification />
            </li>
            <li className="flex">
              <ProfileButton
                value={User?.employeeName as string}
                role={User?.role as string}
                imageUrl={"./images/Avatar.png"}
              />
            </li>
          </ul>
        </nav>
      </header>

      {/* Guide drawer */}
      {guideOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/30"
          onClick={() => setGuideOpen(false)}
        />
      )}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[201] flex flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${guideOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ width: 'min(580px, 95vw)' }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <span className="font-semibold text-sm text-gray-700">Nurse Staff Guide</span>
          <button
            onClick={() => setGuideOpen(false)}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
            aria-label="Close guide"
          >✕</button>
        </div>
        <iframe
          src="/nurse-guide.html"
          className="flex-1 w-full border-none"
          title="Nurse Staff Guide"
        />
      </div>

      {/* Mobile bottom navigation */}
      {!isLoginRoute && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[150] bg-white border-t border-gray-200 flex justify-around items-center py-1">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `flex flex-col items-center px-3 py-1.5 text-[10px] font-medium ${isActive && location === "/" ? "text-frieza" : "text-gray-500"}`
            }
          >
            <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
            Home
          </NavLink>
          <NavLink
            to="/time-off"
            className={({ isActive }) =>
              `flex flex-col items-center px-3 py-1.5 text-[10px] font-medium ${isActive ? "text-frieza" : "text-gray-500"}`
            }
          >
            <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            Time-off
          </NavLink>
          <NavLink
            to="/leaves"
            className={({ isActive }) =>
              `flex flex-col items-center px-3 py-1.5 text-[10px] font-medium ${isActive ? "text-frieza" : "text-gray-500"}`
            }
          >
            <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Leaves
          </NavLink>
          <NavLink
            to="/swap"
            className={({ isActive }) =>
              `flex flex-col items-center px-3 py-1.5 text-[10px] font-medium ${isActive ? "text-frieza" : "text-gray-500"}`
            }
          >
            <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" /></svg>
            Swap
          </NavLink>
        </nav>
      )}
    </>
  );
}
